<template>
<span v-if="!isPopupOpen || !inModal">
	<Form
		@submit="onSubmit"
		@invalid-submit="console.error"
		:validation-schema="schema"
		:initial-values="formValues"
		v-slot="{ errors, meta: meta, }"
	>
	<fieldset :disabled="!isEnabled || !isSupported">
		<legend
			v-if="!inModal"
			class="row form-row g-2 p-0 pb-2 m-0"
		>
			<div class="text-center text-nowrap">Показники лічильника електроенергії</div>
		</legend>

		<div class="row form-row g-0 gx-2 p-0 px-2 my-0">
			<Transition name="description">
				<p v-if="showDescription" class="my-0 paragraph-indent">
					<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" class="bi bi-info-circle" viewBox="0 0 16 16">
						<path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"></path>
						<path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z"></path>
					</svg>
					Відображається поточний початковий показник лічильника електроенергії сервісного модуля, якщо він був кимось встановлений.
					При встановленні нового початкового показника, відбувається його збереження в системі. Потім новий показник записується в сервісний модуль.
					З момента запису в сервісний модуль, імпульси від лічильника інкрементуються до нового показника. Натомість старий ніде не зберігається.
				</p>
			</Transition>
		</div>
		
		<div class="row form-row g-0 gx-2 p-0 px-2 m-0">
			<div class="form-group col-auto my-0">
				<label>Початкові показники, кВт⋅год</label>
				<Field
					:name="`${Station.passportKeys.serviceUnit.self}.${Station.passportKeys.serviceUnit.pwrCntStart}`"
					type="number"
					class="form-control form-control-sm no-arrow"
					:class="{ 'is-invalid': errors[`${Station.passportKeys.serviceUnit.self}.${Station.passportKeys.serviceUnit.pwrCntStart}`] }"
				/>
				<div class="invalid-feedback">{{errors[`${Station.passportKeys.serviceUnit.self}.${Station.passportKeys.serviceUnit.pwrCntStart}`]}}</div>
			</div>
		</div>

		<div class="row form-row gx-2 gb-2 px-2 pb-2 my-1">
			<div class="form-group col-auto ms-auto me-2">
				<button
					type="reset"
					class="btn btn-secondary btn-sm mx-2"
				>
					Відмінити
				</button>
			</div>
			<div class="form-group col-auto me-0">
				<button
					type="submit"
					class="btn btn-primary btn-sm"
					:disabled="!meta.valid"
				>
					<span
						v-if="isPopupOpen"
						class="spinner-border spinner-border-sm"
						role="status"
						aria-hidden="true"
					>
					</span>
					Передати
				</button>
			</div>
		</div>

		<div v-if="isSupported" class="col-12 my-0 py-0">
			<div id="valueHelpBlock" class="form-text">
				<p :class="helpPromtClass" class="mb-0">
					{{ helpPromt }}&nbsp;
				</p>
			</div>
		</div>

		<div
			v-if="!isSupported"
			class="col-12 text-nowrap text-secondary my-0 py-0"
		>
			{{ station?.electricMeterIsSeparateUnit ?
				`*&nbsp;Обраний тип лічильника ${ElectricMeter.versionNames[electricMeterType]} не може виконати цю дію` :
				'*&nbsp;Встановлений сервісний модуль не може виконати цю дію'
			}}
		</div>
	</fieldset>
	</Form>
</span>

<!-- Modal -->
<confirmation-popup
	v-model:show="isPopupOpen"
	:confirm-button-title="isLoading || isError || allRpcs.length > 0 ? '' : 'Так'"
	:reject-button-title="isLoading || isError || allRpcs.length > 0 ? 'Закрити' : 'Ні'"
	@confirm="makeRequest()"
>
	<template v-if="station.isActive" v-slot:body>
		<template v-if="isLoading">
			<span
				class="spinner-border spinner-border-sm"
				role="status"
				aria-hidden="true"
			>
			</span>
			Перевірка активних задач телекерування
		</template>
		<template v-else-if="isError">
			Помилка при перевірці активних задач телекерування, закрийте і спробуйте ще.
		</template>
		<template v-else>
			<template v-if="allRpcs.length > 0">
				Станція зараз обробляє інший запит
				("{{ rpcTaskSchema.job.findJob(allRpcs[0][rpcTaskSchema.job.key]).title }}").
				Будь ласка дочекайтесь його виконання.
			</template>
			<template v-else>
				Ви дійсно хочете перезаписати <b>поточне</b> в сервісному модулі
				і початкове в системі значення лічильника?
			</template>
		</template>
	</template>
	<template v-else v-slot:body>
		Станція не активна. Тільки активні станції виконують запити.
	</template>
</confirmation-popup>
</template>


<script>
import { useAccontStore } from '@/stores/AccountStore.js'
import { useStationsStore } from '@/stores/StationsStore.js'
import {
	rpcTaskSchema,
	stationManageJobs,
	useRpcTasks,
} from '@/composables/rpcTasks.js'
// Validation
// https://jasonwatmore.com/post/2020/10/01/vue-3-veevalidate-form-validation-example
import { Form, Field } from 'vee-validate';
// https://github.com/jquense/yup/tree/a58f02d2f6164c46a9757a818eebac582f7a441c#boolean
import * as Yup from 'yup';
import ConfirmationPopup from "@/components/ConfirmationPopup.vue"
import { Station } from '@/models/station.js'
import { ServiceUnit } from '@/models/serviceUnit.js'
import { ElectricMeter } from '@/models/electricMeter.js'
//import { Station } from '../../station'


export default {
	requiredPromt: 'Обов\'язкове поле',
	overMinPromt: 'Значення меньше мінімального',
	wrongInputPromt: 'Не відповідає вимогам, допускаються тільки числа більше нуля або нуль',

	components: {
		Form,
        Field,
		ConfirmationPopup,
	},

	props: {
		isEnabled: {
			type: Boolean,
			required: false,
			default: true,
		},
		inModal: {
			type: Boolean,
			required: false,
			default: false,
		},
		showDescription: {
			type: Boolean,
			required: false,
			default: true,
		},
		stationId: {
			type: String,
			required: true,
		},
	},

	emits: {
		// Request creation result
		requestResult: value => typeof value === 'boolean',
	},

	setup() {
		const accountStore = useAccontStore()
		const stationsStore = useStationsStore()
		const {
			allRpcs,
			isLoading,
			isError,
			setFilters,
			createRpc,
		} = useRpcTasks()

		return {
			accountStore,
			stationsStore,
			Station,
			ElectricMeter,
			rpcTaskSchema,

			allRpcs,
			isLoading,
			isError,
			setFilters,
			createRpc,
		}
	},

	data() {
		const schema = Yup.object().shape({
			service_unit:  Yup.object().shape({
				pwr_cnt_start_value: Yup.number()
					.required(this.$options.requiredPromt)
					.typeError(this.$options.wrongInputPromt)
					.min(0, this.$options.overMinPromt),
			}),
		})
		return {
			schema,
			rawValues: {},

			// Confirmation popup
			isPopupOpen: false,

			// Help promt
			helpPromtClass: '',
			helpPromt: '',
		}
	},

	mounted () {},

	methods: {
		onSubmit (values) {
			this.rawValues = values
			// Begin fetching active tasks
			this.checkActiveTask()
			this.isPopupOpen = true
		},

		checkActiveTask() {
			this.setFilters({
				destinationStationIds: [this.stationId,],
				isComplete: false,
				jobs: stationManageJobs,
			})
		},

		async makeRequest () {
			if (this.isLoading || this.isError || this.allRpcs.length > 0) {
				return
			}

			const result = await this.writeRpc()
			this.$emit('requestResult', Boolean(result))
		},

		async writeRpc() {
			return await this.createRpc(
				this.accountStore.userId,
				rpcTaskSchema.job.allowed.setPowerCounterStartValue.key,
				this.stationId,
				null,
				{
					value: this.rawValues[Station.passportKeys.serviceUnit.self][Station.passportKeys.serviceUnit.pwrCntStart],
					prevValue: this.formValues[Station.passportKeys.serviceUnit.self][Station.passportKeys.serviceUnit.pwrCntStart],
				}
			)
			.then((result) => {
				if (result) {
					this.printHint('Запит надіслано. Спостерігайте за його станом.')
				} else {
					this.printErr('Сервер відхилив запит постановки завдання.')
				}

				return result
			})
		},

		printHint (msg) {
			this.helpPromtClass = 'text-secondary'
			this.helpPromt = msg
		},

		printErr (msg) {
			this.helpPromtClass = 'text-danger'
			this.helpPromt = msg
		},
	},

	computed: {
		station() { return this.stationsStore.station(this.stationId) },

		electricMeterType() {
			return this.station.passport.get(Station.passportKeys.electricMeter.self)[Station.passportKeys.electricMeter.type]
		},

		/* Check is power counter value suported */
		isSupported() {
			// If service unit is not choose as electric meter
			if (this.electricMeterType != ElectricMeter.versions.impulseInput) {
				return false
			}
			// If no service unit (undefined)
			if (!this.station.passport.has(Station.passportKeys.serviceUnit.self)) {
				return false
			}

			return [
				ServiceUnit.versions['2022-1'],
				ServiceUnit.versions['2022-2'],
				ServiceUnit.versions['2024-1'],
				ServiceUnit.versions['2024-2'],
				ServiceUnit.versions['2025-1'],
				ServiceUnit.versions['2025-2'],
				ServiceUnit.versions['2025-3'],
			].includes(this.station.passport.get(Station.passportKeys.serviceUnit.self)[Station.passportKeys.serviceUnit.unitVersion])
		},

		formValues() {
			return {
				[Station.passportKeys.serviceUnit.self]: {
					[Station.passportKeys.serviceUnit.pwrCntStart]:
						this.station.passport.get(Station.passportKeys.serviceUnit.self)[Station.passportKeys.serviceUnit.pwrCntStart]
				}
			}
		},
	},
}
</script>


<style src="@/assets/stationForm.css" scoped></style>
<style scoped>
/* Remove arrows from input Chrome, Safari, Edge, Opera */
input.no-arrow::-webkit-outer-spin-button,
input.no-arrow::-webkit-inner-spin-button {
	-webkit-appearance: none;
	margin: 0;
}

/* Remove arrows from input Firefox */
input[type=number].no-arrow {
	appearance: textfield;
	-moz-appearance: textfield;
}
</style>